import { z } from "zod";
import type { Estimate } from "./estimate.js";
import { TradeSeedKind, WorkType } from "./estimateAxes.js";
import { TaxCategory } from "./tax.js";
import type { LinePhotoList } from "./phoneEstimate.js";
import { evalTakeoff } from "./takeoff.js";

// ---------------------------------------------------------------------------
// 写真の引き出し（場所 → 品目 → 採寸）と 設定の品目（#118、docs/47 §4・§11、docs/44 §5、DB 20261004_1500）。
// 撮った写真を ①場所（見積の estimate_places。会社の雛形から写す・その場で作る）②品目（items、いくつでも）③採寸（拾い出し）に振り分け、
// 「決定」で 選んだ 場所 × 品目 ごとに見積の行を作る（仮。同じ場所 × 同じ品目の行があればその行を使う）。
// 写真は選んだ行すべてに付く（実体は1つ・結び付き estimate_photo_links が増える）。採寸は行ごとに拾い出しを1本ずつ。
// 単価・金額はスマホに出さない（docs/44 §1 の7）。決定直後のトーストの「元に戻す」で、作った行・拾い出し・結び付きを取り消す
// ---------------------------------------------------------------------------

/** 写真の引き出しの向き（accounts.drawer_hand）。right＝右手（既定）／left＝左手 */
export const DrawerHand = z.enum(["right", "left"]);
export type DrawerHand = z.infer<typeof DrawerHand>;

/** 画面に出す文（docs/10 §5.1.2）。{n} などは呼ぶ側で入れる */
export const PHOTO_ASSIGN_MSG = {
  // S4 場所を選ぶ
  pickPlace: "場所を選ぶ",
  photos: "写真 {n}枚",
  rows: "{n}行",
  addPlace: "場所追加",
  placeName: "場所の名前",
  placeNameHelp: "「2F ＞ 子供部屋」のように ＞ で区切ると2段になります。この見積の場所として足します。",
  recent: "最近使った",
  same: "前と同じ",
  later: "あとで分類",
  close: "閉じる",
  // S5 品目を選ぶ
  pickItems: "品目を選ぶ（いくつでも）",
  allTrades: "すべて",
  noItems: "品目がありません。「品目追加」から足します",
  addItem: "品目追加",
  itemName: "品目の名前",
  trade: "{trade}",
  noTrade: "{trade}なし",
  next: "次へ（{n}件）",
  decideNoMeasure: "採寸せずに決定",
  hasLine: "{n}行あり",
  // S6 採寸
  measure: "採寸（任意）",
  intoLines: "{n}行に入れます",
  unit: "単位",
  /** 採寸の単位のボタンに並ばない単位を選ぶ（#164。会社の単位の一覧の残り） */
  unitMore: "ほか…",
  note: "ひとこと（社内）",
  decide: "決定",
  clear: "クリア",
  backspace: "1文字削除",
  formulaInvalid: "式が読めません（数字と ＋ − × ÷ だけ）",
  back: "戻る",
  // 決定のあと
  assigned: "「{place}」に {n}行入れました",
  queued: "送り待ちに入れました。電波が戻ったら送ります",
  undone: "元に戻しました",
  undoFailed: "元に戻せませんでした",
  pendingAssign: "振り分けの送り待ち {n}件",
  // 行に入れていない写真から
  assignPhotos: "振り分け（{n}枚）",
  selectPhoto: "写真選択",
  // 設定
  /** 使う機能で出しているタブだけで題を組むときは axesSettingsTitle（#142 段6 No.26） */
  settingsTitle: "{trade}・場所・品目",
  tabTrades: "{trade}",
  tabPlaces: "場所",
  tabItems: "品目",
  item: "品目",
  workType: "材工区分",
  defaultUnit: "既定の単位",
  /** #142 段6 No.25 */
  vendor: "いつもの{vendor}",
  noVendor: "なし",
  addItemSubmit: "品目追加",
  saveItem: "品目保存",
  itemAdded: "品目「{name}」を追加しました",
  itemSaved: "品目を保存しました",
  // 個人設定
  hand: "写真の引き出し",
  handLabel: { right: "右手（右から出す）", left: "左手（左から出す）" },
  /** 個人設定のセグメント（#152、docs/61 ③⑧。行が狭いので短く。読み上げの名前は handLabel） */
  handShort: { right: "右手", left: "左手" },
  handSaved: "写真の引き出しを「{hand}」にしました",
  /** API のエラー */
  placeRequired: "場所を選んでください",
  itemsRequired: "品目を選んでください",
  assignNotFound: "その振り分けは見つかりません",
  alreadyUndone: "この振り分けはもう元に戻しています",
  photoNotFound: "その写真は見つかりません",
  itemNotFound: "その品目は見つかりません",
  itemNameInvalid: "品目の名前は1〜100文字です",
  itemTradeInvalid: "その{trade}は見つかりません",
  itemVendorInvalid: "いつもの{vendor}にできるのは「{vendor}」の取引先だけです",
  lineItemInvalid: "その品目は見つかりません",
  unitMismatch: "「{name}」の単位は {unit} です。単位を {unit} にしてください",
  itemInUseArchived: "見積で使っているので、消さずに「使わない」にしました",
} as const;

const Uuid = z.string().uuid();
const Version = z.string().min(1).max(40);
const PlaceName = z.string().trim().min(1, "名前を入れてください").max(50, "名前は50文字までです");
const ItemName = z.string().trim().min(1, PHOTO_ASSIGN_MSG.itemNameInvalid).max(100, PHOTO_ASSIGN_MSG.itemNameInvalid);
const Unit = z.string().trim().max(20);

/** 採寸（拾い出し1本分）。式は読めること（evalTakeoff。DB の fn_takeoff_eval と同じ決まり） */
export const PhotoAssignTakeoff = z.object({
  formula: z.string().trim().min(1).max(200).refine((f) => evalTakeoff(f) != null, PHOTO_ASSIGN_MSG.formulaInvalid),
  unit: Unit.default(""),
  memo: z.string().trim().max(200).default(""),
});
export type PhotoAssignTakeoff = z.infer<typeof PhotoAssignTakeoff>;

/**
 * POST /estimates/:id/photo-assign（決定）。
 *   場所：placeId（この見積の場所か、会社の雛形＝見積へ写す）か newPlace（この見積に作る。parentName で2段目）のどちらか1つ。
 *   品目：itemIds（品目マスタ）と newItems（その場で品目マスタに足す。同じ名前があればそれを使う）。合わせて1つ以上。
 *   写真：photoIds（送った写真の id）と photoClientUuids（送り待ちから送った写真の clientUuid。見つからない分は飛ばす）。0枚でもよい。
 *   takeoff を省けば「採寸せずに決定」（行と写真だけ。新しい行の数量は空）。note＝ひとこと（写真のひとことが空なら入れる・拾い出しのメモ）。
 *   version は Estimate.updatedAt（違えば 409 ESTIMATE_STALE）。clientUuid が同じ決定は2回入れない（前の答えを返す）
 */
export const PhotoAssign = z
  .object({
    version: Version,
    clientUuid: Uuid.optional(),
    photoIds: z.array(Uuid).max(50).default([]),
    photoClientUuids: z.array(Uuid).max(50).default([]),
    placeId: Uuid.optional(),
    newPlace: z.object({ name: PlaceName, parentName: PlaceName.optional() }).optional(),
    itemIds: z.array(Uuid).max(20).default([]),
    newItems: z.array(z.object({ name: ItemName, tradeId: Uuid.nullable().optional(), unit: Unit.optional() })).max(20).default([]),
    takeoff: PhotoAssignTakeoff.optional(),
    note: z.string().trim().max(200).optional(),
  })
  .refine((x) => !!x.placeId !== !!x.newPlace, { message: PHOTO_ASSIGN_MSG.placeRequired, path: ["placeId"] })
  .refine((x) => x.itemIds.length + x.newItems.length >= 1, { message: PHOTO_ASSIGN_MSG.itemsRequired, path: ["itemIds"] });
export type PhotoAssign = z.input<typeof PhotoAssign>;

/** 決定で入った行（created＝新しく作った仮の行／false＝同じ場所 × 同じ品目の行に足した） */
export const PhotoAssignLine = z.object({ lineId: Uuid, lineageId: Uuid, itemId: Uuid.nullable(), name: z.string(), created: z.boolean() });
export type PhotoAssignLine = z.infer<typeof PhotoAssignLine>;

/** POST /estimates/:id/photo-assign の答え（送り直しは前の答えと同じ assignId・lines） */
export type PhotoAssignResult = {
  assignId: string;
  placeId: string;
  placeName: string;
  lines: PhotoAssignLine[];
  takeoffCount: number;
  linkCount: number;
  /** 送り待ちの写真のうち、見つからなかった（断られた）枚数 */
  missingPhotos: number;
  estimate: Estimate;
  photos: LinePhotoList;
};

/** POST /estimates/:id/photo-assign/:assignId/undo（元に戻す）。版が違えば 409 ESTIMATE_STALE（「元に戻せませんでした」） */
export const PhotoAssignUndo = z.object({ version: Version });
export type PhotoAssignUndo = z.infer<typeof PhotoAssignUndo>;
export type PhotoAssignUndoResult = { estimate: Estimate; photos: LinePhotoList };

/* ---------------- 品目マスタ（設定 → 工種・場所・品目） ---------------- */

/** 品目（GET /items）。unit＝既定の単位、workType＝材工区分、vendorPartnerId＝いつもの外注先。archived＝使わない（引き出しに出さない） */
export const Item = z.object({
  id: Uuid,
  name: z.string(),
  unit: z.string(),
  tradeId: Uuid.nullable(),
  workType: WorkType.nullable(),
  vendorPartnerId: Uuid.nullable(),
  /** 税区分（T2。既定 standard）。候補の引き出し・写真の引き出しで選ぶと明細の行に写る */
  taxCategory: TaxCategory,
  sortNo: z.number().int(),
  archived: z.boolean(),
});
export type Item = z.infer<typeof Item>;
export const ItemList = z.object({ items: z.array(Item) });
export type ItemList = z.infer<typeof ItemList>;

/** POST /items（管理者） */
export const ItemCreate = z.object({
  name: ItemName,
  unit: Unit.default(""),
  tradeId: Uuid.nullable().default(null),
  workType: WorkType.nullable().default(null),
  vendorPartnerId: Uuid.nullable().default(null),
  /** 税区分（T2。新コード7つだけ。省けば standard） */
  taxCategory: TaxCategory.default("standard"),
});
export type ItemCreate = z.input<typeof ItemCreate>;
/** PUT /items/:id（管理者）。省いた欄は変えない */
export const ItemUpdate = z.object({
  name: ItemName.optional(),
  unit: Unit.optional(),
  tradeId: Uuid.nullable().optional(),
  workType: WorkType.nullable().optional(),
  vendorPartnerId: Uuid.nullable().optional(),
  taxCategory: TaxCategory.optional(),
  archived: z.boolean().optional(),
});
export type ItemUpdate = z.infer<typeof ItemUpdate>;
/** POST /items/seed（管理者）。分類の見本と同じキー。kind を省くと会社の業種（#142 段4） */
export const ItemSeed = z.object({ kind: TradeSeedKind.optional() });
export type ItemSeed = z.infer<typeof ItemSeed>;

/* ---------------- 採寸のテンキー（画面の S6。式を1文字ずつ組み立てる） ---------------- */

/** テンキーのキー。数字・小数点・＋−×÷・1文字消す・クリア */
export type KeypadKey = "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "." | "×" | "÷" | "+" | "−" | "back" | "clear";
const OPS = ["×", "÷", "+", "−"];

/**
 * テンキーで式を組み立てる（式は「12.5×9.6」の形で持つ。DB と evalTakeoff は × ÷ − をそのまま読む）。
 *   ・演算子のあとに演算子 → 置き換える（「12×+」は「12+」）。頭に置けるのは −（マイナスの数）だけ
 *   ・小数点は1つの数に1つ。数の頭なら「0.」
 *   ・200文字まで
 */
export function keypadInput(formula: string, key: KeypadKey): string {
  if (key === "clear") return "";
  if (key === "back") return [...formula].slice(0, -1).join("");
  if (formula.length >= 200) return formula;
  const last = formula.slice(-1);
  if (OPS.includes(key)) {
    if (!formula) return key === "−" ? "−" : "";
    if (OPS.includes(last)) return formula.length === 1 ? (key === "−" ? "−" : "") : formula.slice(0, -1) + key;
    if (last === ".") return formula.slice(0, -1) + key;
    return formula + key;
  }
  if (key === ".") {
    const seg = formula.split(/[×÷+−]/).pop() ?? "";
    if (seg.includes(".")) return formula;
    return formula + (seg === "" ? "0." : ".");
  }
  // 数字：「0」だけの数のあとに数字を足すときは 0 を置き換える（「07」にしない）
  const seg = formula.split(/[×÷+−]/).pop() ?? "";
  if (seg === "0") return formula.slice(0, -1) + key;
  return formula + key;
}

/**
 * 設定の「{trade}・場所・品目」の題を、使う機能で出しているタブだけで組む（#142 段6 No.26。営業力の既定＝「{trade}・品目」）。
 * 3つとも OFF のときは今までの題（メニューに出さないが、URL で開いたとき）
 */
export function axesSettingsTitle(on: { trades: boolean; places: boolean; items: boolean }): string {
  const all: Array<string | null> = [on.trades ? PHOTO_ASSIGN_MSG.tabTrades : null, on.places ? PHOTO_ASSIGN_MSG.tabPlaces : null, on.items ? PHOTO_ASSIGN_MSG.tabItems : null];
  const parts = all.filter((x): x is string => !!x);
  return parts.length ? parts.join("・") : PHOTO_ASSIGN_MSG.settingsTitle;
}
